import { Key } from 'react'
import { Button, PopConfirm } from '@pepper/ui'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { ColumnsType } from 'antd/lib/table'

import PageUrl from 'container/SEOTools/ContentRefresh/PageUrl'
import { formatLongNumber } from 'utils/functions/formatLongNumber'
import { RefreshablePagesList } from '../../types'

import s from './Table.module.css'

type Args = {
  handleDelete: (reportId: string) => void
  handleGenerateGapReport: (records: RefreshablePagesList[]) => void
  isGeneratingReports: boolean
  generatingIds: string[]
  totalRecords: number
  selectedRowKeys: Key[]
  domainName?: string
}

export const getRefreshOpportunitiesColumn = <
  RecordType extends RefreshablePagesList
>({
  domainName = '',
  handleDelete,
  handleGenerateGapReport,
  isGeneratingReports,
  totalRecords,
  generatingIds,
  selectedRowKeys,
}: Args): ColumnsType<RecordType> => {
  return [
    {
      key: 'page',
      title: () => (
        <div className={s.title}>Identified Pages ({totalRecords})</div>
      ),
      dataIndex: 'page',
      ellipsis: true,
      render: (_, record) => {
        return record.title.length > 0 ? record.title : '_'
      },
    },
    {
      key: 'page_url',
      title: () => <div className={s.title}>Page URL</div>,
      dataIndex: 'page_url',
      render: (_, record) => (
        <PageUrl url={record.page_url} domainName={domainName} />
      ),
    },
    {
      key: 'potential_gain',
      title: () => (
        <div className={clsx(s.rightAlign, s.title)}>Potential Gain</div>
      ),
      dataIndex: 'potential_gain',
      width: 130,
      render: (_, record) => (
        <div className={s.potentialGain}>
          {formatLongNumber(record.potential_gain ?? 0, 1)}
          <Icon icon="lucide:arrow-up-right" height={16} />
        </div>
      ),
    },
    {
      key: 'quick_win',
      title: '',
      dataIndex: 'quick_win',
      width: 480,
      render: (_, record, index) => {
        return (
          <div className={s.opportunityLabelWrapper}>
            <div
              className={clsx([s.badge], {
                [s.quickWin]: record.quick_win,
                [s.refresh]: !record.quick_win,
              })}
            >
              {record.quick_win ? 'Quick Win' : 'Refresh Opportunity'}
            </div>
            <div className={s.btnWrapper}>
              <Button
                type="link"
                disabled={(record?.content_gap_report_id ?? '')?.length > 0}
                onClick={() => {
                  handleGenerateGapReport([record])
                }}
                data-id={`page-${index + 1}-generate-gap-report-button`}
              >
                {isGeneratingReports && generatingIds.includes(record.id) ? (
                  <>
                    <div className={s.generatingReport}>Generating Report</div>
                    <Icon
                      icon="lucide:loader"
                      color="#2e3338"
                      className={s.loader}
                    />
                  </>
                ) : (
                  <>
                    Generate Gap Report
                    <Icon icon="mingcute:right-line" />
                  </>
                )}
              </Button>

              <PopConfirm
                title="Are you sure about dismiss this page?"
                onConfirm={() => {
                  handleDelete(record.id)
                }}
                okText="Dismiss"
                cancelText="Cancel"
                placement="topRight"
                disabled={selectedRowKeys.length > 0}
              >
                <div
                  className={clsx(s.delete, {
                    [s.disabled]: selectedRowKeys.length > 0,
                  })}
                  title="Dismiss"
                >
                  <Icon icon="gg:remove" />
                </div>
              </PopConfirm>
            </div>
          </div>
        )
      },
    },
  ]
}
